import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddActionButton from '../../../components/designe/ActionButtons/AddActionButton';
import { AppBarWithButtons } from '../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppFormSelectExercise from '../../../components/designe/forms/app-form-select-exercise/AppFormSelectExercise';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import SetGroupEditTable from '../../../components/reusable/set-group-edit-table/SetGroupEditTable';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import withRouter from '../../../hoc/with-router';
import { useScrollToFirstError } from '../../../hooks/use-scroll-to-first-error';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { FormMode } from '../../../shared/constants';
import { IExercise } from '../../../types/interfaces/exercise';
import { deepClone } from '../../../utils/collections/array-utils';
import { NewExerciseModal } from '../../exercises/new-exercise/NewExerciseModal';
import { useStyles } from './EditProgramSet.styles';
import { useEditProgramSetComponent } from './useEditProgramSetComponent';
import useEditTable from './useEditTable';

const EditProgramSet = (props) => {
  const { params } = props;
  const { t } = useTranslation();
  const { back } = useNavigationStack();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const {
    formik,
    isLoading,
    newProgramSetId,
    onChangeExercise,
    showNewExerciseModal,
    setShowNewExerciseModal,
  } = useEditProgramSetComponent(params.setId);
  const sets = useMemo(
    () => [deepClone(formik.values)],
    [JSON.stringify(formik.values)],
  );
  const useSetGroupEditTable = useEditTable(formik);
  const { allowAndLeave, dialog } = useUnsavedChangesGuard({
    isDirty: formik.dirty && !formik.isSubmitting,
    leaveAction: back,
  });

  useScrollToFirstError(formik, {
    fieldOrder: ['title', 'description', 'programSetItems'],
  });

  return (
    <PageContainer
      title={
        !params.setId || params.setId === newProgramSetId
          ? t('programs.newProgramSet')
          : t('programs.editProgramSet')
      }
      backButton>
      <div className={classes.addProgramSetWrapper}>
        {dialog}
        <form
          onSubmit={formik.handleSubmit}
          className={classes.addProgramSetForm}>
          <Box display="flex" gap={1} flexDirection="column" marginBottom={1}>
            <AppFormTextField
              label={t('common.title')}
              fullWidth
              autoFocus
              name="title"
              formik={formik}
            />

            <AppFormTextField
              label={t('common.description')}
              fullWidth
              multiline
              minRows={3}
              maxRows={15}
              name="description"
              formik={formik}
            />
          </Box>
          <Box data-formik-field="programSetItems">
            <SetGroupEditTable
              useSetGroupEditTable={useSetGroupEditTable}
              sets={sets}
            />
          </Box>
          <Box className={classes.addProgramSetButtonWrapper}>
            <AppBarWithButtons
              loading={isLoading}
              onCancelClick={allowAndLeave}
              submitButtonProps={{ 'data-onboarding': 'newTraining.save' }}
            />
          </Box>
        </form>
      </div>
      <NewExerciseModal
        showNewExerciseModal={showNewExerciseModal}
        setShowNewExerciseModal={setShowNewExerciseModal}
        onExerciseCreate={(exercise: IExercise) => onChangeExercise(exercise)}
      />
    </PageContainer>
  );
};

export default withRouter(EditProgramSet);
